import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  capitalize,
  errors,
  IExtendableDto,
  IGridAttributes,
  INotificationDto,
  isNullOrEmptySpace,
  Log,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  RydeAppUnit,
  SINGLE_KEY,
  useHasPermissions,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../common";
import { useOpenEntityFormCellTemplate } from "../templates-edit/hooks";
import { IRenderColumnTemplate } from "./_core";

const TOOLTIP_MOUTH_LEAVE_DELAY = 300;

const resolveRelateToEntity = ({
  rowOriginal,
  relateToEntity,
}: {
  rowOriginal: unknown;
  relateToEntity: IGridAttributes["relateToEntity"];
}): string => {
  if (relateToEntity === "notification_dynamic") {
    const notification = rowOriginal as INotificationDto;
    return notification.entityName;
  }

  return String(relateToEntity);
};

const resolveEntityId = ({
  rowOriginal,
  name,
  relateToEntity,
}: {
  rowOriginal: unknown;
  name: string;
  relateToEntity: IGridAttributes["relateToEntity"];
}): number => {
  if (relateToEntity === "notification_dynamic") {
    const notification = rowOriginal as INotificationDto;
    return notification.entityRecordId;
  }

  return (rowOriginal as IExtendableDto)[name] as number;
};

const resolveValue = ({
  rowOriginal,
  relateToEntity,
  valueComputed,
}: {
  rowOriginal: unknown;
  relateToEntity: IGridAttributes["relateToEntity"];
  valueComputed: unknown;
}): string => {
  if (relateToEntity === "notification_dynamic") {
    const notification = rowOriginal as INotificationDto;
    return notification.entityName;
  }

  return !isNullOrEmptySpace(valueComputed as string)
    ? String(valueComputed)
    : NO_VALUE_DISPLAY;
};

export const OpenEntityFormCellTemplate: FC<IRenderColumnTemplate> = ({
  gridAttributes,
  rowOriginal,
  name,
  value,
  valueTranslated,
}) => {
  const { t } = useTranslation();
  const { openEntityForm } = useOpenEntityFormCellTemplate();
  const valueComputed = valueTranslated ?? value;
  const isShowId = gridAttributes?.showId;

  const entityName = resolveRelateToEntity({
    rowOriginal,
    relateToEntity: gridAttributes?.relateToEntity,
  });

  const { hasPermissions } = useHasPermissions({
    entity: entityName as RydeAppUnit,
    action: "update",
  });
  const entityId = resolveEntityId({
    name,
    rowOriginal,
    relateToEntity: gridAttributes?.relateToEntity,
  });
  const valueLabel = resolveValue({
    valueComputed,
    relateToEntity: gridAttributes?.relateToEntity,
    rowOriginal,
  });

  const onClickHandler = (): void => {
    if (!hasPermissions) {
      Log.warnAndToast(t(errors.common.genericNoPermission));
      return;
    }

    openEntityForm({
      entity: entityName,
      id: entityId,
    });
  };

  const tooltipContent = (): ReactNode => {
    return (
      <span>
        {capitalize(t(`${entityName}.${SINGLE_KEY}`))} {": "}
        <span
          className="pointer"
          onClick={onClickHandler}
          onKeyDown={onActivateKeyDown(onClickHandler)}
          role="button"
          tabIndex={0}
          style={{
            color: rydeColors.dragBorderColor,
            textDecoration: "underline",
          }}
        >
          {isShowId && `${t("general.ID")}:${entityId} | `}
          {valueLabel}
        </span>
      </span>
    );
  };

  return (
    <div>
      <FramerTooltip
        content={tooltipContent()}
        rtlStrippedBottomLeftPlacement
        mouseLeaveDelay={TOOLTIP_MOUTH_LEAVE_DELAY}
      >
        {valueLabel}
      </FramerTooltip>
    </div>
  );
};
